import { useCallback } from 'react';

import { useIntl } from 'react-intl';
import {
	createHook,
	createStore,
	type BoundActions,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema';

import { appendADFContent } from '../../common/utils/adf';

import messages from './messages';

type State = {
	highlightActionsEditorPlaceholder: DocNode | null;
};

const state = createStore({
	initialState: {
		highlightActionsEditorPlaceholder: null,
	} as State,
	actions: {
		setHighlightActionsEditorPlaceholder:
			(
				highlightActionsEditorPlaceholder: State['highlightActionsEditorPlaceholder'],
			): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ highlightActionsEditorPlaceholder });
			},
	},
});

// exported for testing
export const useHighlightActionsEditorPlaceholderStore: HookFunction<
	State,
	BoundActions<
		State,
		{
			setHighlightActionsEditorPlaceholder: (
				highlightActionsEditorPlaceholder: State['highlightActionsEditorPlaceholder'],
			) => ({ setState }: StoreActionApi<State>) => void;
		}
	>,
	void
> = createHook(state);

const getHighlightActionPlaceholderData = (prompt: DocNode, placeholderText: string) => {
	const promptContent = appendADFContent(prompt, placeholderText);
	return promptContent;
};

export const useHighlightActionsEditorPlaceholder = (): {
	highlightActionsEditorPlaceholder: DocNode | null;
	insertHighlightActionsEditorPlaceholder: (
		prompt: DocNode,
		options?: { skipPlaceholderPrompt?: boolean },
	) => void;
	clearHighlightActionsEditorPlaceholder: () => void;
} => {
	const [{ highlightActionsEditorPlaceholder }, { setHighlightActionsEditorPlaceholder }] =
		useHighlightActionsEditorPlaceholderStore();
	const { formatMessage } = useIntl();

	const insertHighlightActionsEditorPlaceholder = useCallback(
		(prompt: DocNode, options?: { skipPlaceholderPrompt?: boolean }): void => {
			// When `skipPlaceholderPrompt` is set, seed the editor with the prompt
			// exactly as provided instead of appending the highlight-action
			// "Ask a question about this." trailer. This lets non-highlight callers
			// (e.g. a prompt supplied via URL param) pre-fill the input as an
			// editable draft without injecting extra text.
			const placeholderData = options?.skipPlaceholderPrompt
				? prompt
				: getHighlightActionPlaceholderData(
						prompt,
						formatMessage(messages.highlightActionsPlaceholderPrompt),
					);

			setHighlightActionsEditorPlaceholder(placeholderData);
		},
		[setHighlightActionsEditorPlaceholder, formatMessage],
	);

	const clearHighlightActionsEditorPlaceholder = useCallback((): void => {
		setHighlightActionsEditorPlaceholder(null);
	}, [setHighlightActionsEditorPlaceholder]);

	return {
		highlightActionsEditorPlaceholder,
		insertHighlightActionsEditorPlaceholder,
		clearHighlightActionsEditorPlaceholder,
	};
};
